import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Undo2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Segmented } from "@/components/ui/segmented";
import { confirmSheet } from "@/components/ui/action-sheet";
import { api, errorMessage } from "@/lib/api";
import { btnGold, btnLine, fmtAt, inp } from "@/lib/crm";

const MODES = [["skip", "Skip duplicates"], ["update", "Update existing"], ["anyway", "Import anyway"]];
const ACTION = { create: ["New", "text-[#248A3D]"], update: ["Update", "text-[#0062CC]"], skip: ["Skip", "text-[#D70015]"] };

const saveBlob = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); };

async function downloadTemplate(ext) {
  try { saveBlob((await api.get(`/customers/import/template.${ext}`, { responseType: "blob" })).data, `customer_import_template.${ext}`); }
  catch (e) { toast.error(errorMessage(e)); }
}

function Defaults({ people, d, setD }) {
  const [presets, setPresets] = useState([]);
  useEffect(() => { api.get("/markup-presets").then((r) => setPresets(r.data)).catch(() => {}); }, []);
  const lab = "space-y-1 text-[11px] uppercase tracking-wider text-slate-500";
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="import-defaults">
      {people.me.role === "admin" && (
        <label className={lab}>Default showroom
          <select data-testid="import-default-showroom" value={d.showroom_id} onChange={(e) => setD({ ...d, showroom_id: e.target.value })} className={inp}>
            <option value="">Main showroom</option>{people.showrooms.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></label>
      )}
      <label className={lab}>Default sales rep
        <select data-testid="import-default-rep" value={d.rep_id} onChange={(e) => setD({ ...d, rep_id: e.target.value })} className={inp}>
          <option value="">Me</option>{people.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select></label>
      <label className={lab}>Default markup preset
        <select data-testid="import-default-preset" value={d.preset_id} onChange={(e) => setD({ ...d, preset_id: e.target.value })} className={inp}>
          <option value="">Company default</option>{presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select></label>
    </div>
  );
}

function Mapping({ pv, mapping, setMapping }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="import-mapping">
      {pv.fields.map((f) => (
        <label key={f.key} className="space-y-1 text-[11px] uppercase tracking-wider text-slate-500">{f.label}{f.key === "name" ? " *" : ""}
          <select data-testid={`import-map-${f.key}`} value={mapping[f.key] ?? ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value === "" ? undefined : Number(e.target.value) })} className={inp}>
            <option value="">Not in file</option>{pv.columns.map((c, i) => <option key={i} value={i}>{c}</option>)}
          </select></label>
      ))}
    </div>
  );
}

function PreviewRows({ pv }) {
  return (
    <div className="max-h-72 overflow-auto rounded-[12px] border border-black/[0.08] bg-white" data-testid="import-preview-rows">
      <table className="w-full min-w-[640px] text-[13px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6D6D72]">{["Row", "Name", "Email", "Phone", "Result"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{pv.rows.map((r, i) => (
          <tr key={r.row} data-testid={`import-preview-row-${i}`} className="border-t border-black/[0.06] align-top">
            <td className="px-3 py-2 text-[#6D6D72]">{r.row}</td><td className="px-3 py-2 text-black">{r.name || "—"}</td>
            <td className="px-3 py-2">{r.email}</td><td className="px-3 py-2">{r.phone}</td>
            <td className="px-3 py-2"><span className={`font-semibold ${ACTION[r.action][1]}`}>{ACTION[r.action][0]}{r.match_name && r.action === "update" ? ` ${r.match_name}` : ""}</span>
              {r.reason && <span className="block text-[#6D6D72]">{r.reason}</span>}
              {r.warnings.map((w) => <span key={w} className="block text-[#8A4B00]">{w}</span>)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function Result({ res, onClose }) {
  return (
    <div className="space-y-4" data-testid="import-result">
      <p data-testid="import-result-summary" className="text-[17px] font-semibold text-black">Imported {res.imported}, updated {res.updated}, skipped {res.skipped}</p>
      {res.skipped > 0 && <button data-testid="import-skipped-download" onClick={() => saveBlob(new Blob([res.skipped_csv], { type: "text/csv" }), "skipped_customers.csv")} className={btnLine}><Download className="h-4 w-4" /> Download skipped rows</button>}
      <button data-testid="import-done-button" onClick={onClose} className={btnGold}>Done</button>
    </div>
  );
}

export function CustomerImport({ people, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("skip");
  const [d, setD] = useState({ showroom_id: "", rep_id: "", preset_id: "" });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const form = (useMap) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dup_mode", mode);
    Object.entries(d).forEach(([k, v]) => fd.append(k, v));
    if (useMap) fd.append("mapping", JSON.stringify(Object.fromEntries(Object.entries(mapping).filter(([, v]) => v !== undefined))));
    return fd;
  };
  useEffect(() => {
    if (!file) return;
    let live = true;
    setBusy(true);
    api.post("/customers/import/preview", form(!!pv)).then((r) => { if (!live) return; setPv(r.data); if (!pv) setMapping(r.data.mapping); })
      .catch((e) => { if (!live) return; toast.error(errorMessage(e)); if (!pv) setFile(null); }).finally(() => live && setBusy(false));
    return () => { live = false; };
  }, [file, mapping, mode, d]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/customers/import", form(true)); setRes(data); onDone(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-import-dialog" className="max-w-4xl">
        <DialogHeader><DialogTitle className="text-[22px] font-bold">Import customers</DialogTitle>
          <DialogDescription>Excel (.xlsx, .xls) or CSV, up to 10 MB. Rows without a name are skipped.</DialogDescription></DialogHeader>
        {res ? <Result res={res} onClose={onClose} /> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <label className={`${btnGold} cursor-pointer`} data-testid="import-file-label"><Upload className="h-4 w-4" />{file ? "Choose another file" : "Choose file"}
                <input data-testid="import-file-input" type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { setPv(null); setFile(e.target.files?.[0] || null); e.target.value = ""; }} /></label>
              {file && <span className="inline-flex items-center gap-1.5 text-[15px] text-black"><FileSpreadsheet className="h-4 w-4 text-[#248A3D]" />{file.name}</span>}
              <span className="ml-auto flex gap-2 text-[15px]">Template:
                <button data-testid="import-template-xlsx" onClick={() => downloadTemplate("xlsx")} className="text-[#0062CC]">Excel</button>
                <button data-testid="import-template-csv" onClick={() => downloadTemplate("csv")} className="text-[#0062CC]">CSV</button></span>
            </div>
            {pv && <>
              <Mapping pv={pv} mapping={mapping} setMapping={setMapping} />
              <Defaults people={people} d={d} setD={setD} />
              <div className="flex flex-wrap items-center gap-3"><span className="text-[15px] text-black">Duplicates (same email or phone)</span>
                <Segmented testId="import-dup-mode" value={mode} options={MODES} onChange={setMode} /></div>
              <p data-testid="import-preview-counts" className="text-[15px] text-[#3C3C43]">{pv.total} rows: {pv.counts.create} new, {pv.counts.update} to update, {pv.counts.skip} skipped{pv.counts.warnings ? `, ${pv.counts.warnings} with warnings` : ""}</p>
              <PreviewRows pv={pv} />
              <button data-testid="import-run-button" disabled={busy || mapping.name === undefined} onClick={run} className={btnGold}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}Import {pv.counts.create + pv.counts.update} customers</button>
            </>}
            {busy && !pv && <Loader2 className="h-5 w-5 animate-spin text-[#007AFF]" />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function LastImport({ tick, onUndone }) {
  const [li, setLi] = useState(null);
  useEffect(() => { api.get("/customers/import/last").then((r) => setLi(r.data)).catch(() => setLi(null)); }, [tick]);
  if (!li) return null;
  const undo = async () => {
    const msg = li.kept ? `${li.kept} of them already have projects and will stay.` : "Updated customers keep their changes.";
    if (!(await confirmSheet({ title: `Undo import of ${li.file_name}?`, message: `Removes ${li.removable} customers this import created. ${msg}`, confirm: "Undo Import", destructive: true }))) return;
    try { const { data } = await api.post(`/customers/import/${li.id}/undo`); toast.success(`Removed ${data.removed} imported customers${data.kept ? `, kept ${data.kept} with projects` : ""}`); onUndone(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div data-testid="last-import" className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-white px-4 py-3 text-[15px]">
      <span className="text-black">Last import: <span className="font-semibold">{li.file_name}</span> · {li.imported} new, {li.updated} updated · {fmtAt(li.at)}</span>
      <button data-testid="last-import-undo" onClick={undo} className="inline-flex min-h-[44px] items-center gap-1.5 text-[#D70015]"><Undo2 className="h-4 w-4" /> Undo</button>
    </div>
  );
}
